/**
 * Client-side autosave for the New Job form.
 *
 * Persists the operator's in-progress inputs to localStorage so navigating
 * away (e.g. to Settings) or refreshing the page doesn't lose typed work.
 * The draft is cleared on a successful Start / Auto-scan / Find-scene /
 * Save-as-draft, and by the form's explicit Clear button.
 *
 * Framework-free and side-effect-isolated on purpose: the form talks to the
 * three functions below and nothing else, so the storage backend (or the
 * "what counts as content" rule) can change without touching the component.
 * Every storage access is guarded — private-mode / disabled / quota-full
 * storage degrades to a no-op and the form behaves exactly as it did before
 * persistence existed.
 */

export interface NewJobDraft {
  url: string;
  from: string;
  endMode: "end" | "duration";
  to: string;
  duration: string;
  startPhrase: string;
  endPhrase: string;
  host: string;
  guests: string;
  topic: string;
  autoCaption: boolean;
  autoSocial: boolean;
  autoScanMode: boolean;
  locateMode: boolean;
  openingLine: string;
  closingLine: string;
  scanCount: number;
  musicTrack: string;
}

// Versioned key: a future shape change can bump the suffix to retire stale
// drafts cleanly instead of trying to migrate half-written objects.
const STORAGE_KEY = "ql.newjob.draft.v1";

/** The text fields that make a draft worth keeping. Toggles, end-mode, the
 *  randomly-seeded music pick, and scan count are remembered but don't, on
 *  their own, count as "the operator has work in progress here". */
const CONTENT_FIELDS: Array<keyof NewJobDraft> = [
  "url", "from", "to", "duration", "startPhrase", "endPhrase",
  "host", "guests", "topic", "openingLine", "closingLine",
];

/**
 * True when the draft holds at least one non-blank text field — i.e. there's
 * real work to preserve. Single source of truth shared by the autosave effect
 * (save vs. clear) and the Clear button's visibility.
 */
export function draftHasContent(d: Partial<NewJobDraft>): boolean {
  return CONTENT_FIELDS.some((k) => {
    const v = d[k];
    return typeof v === "string" && v.trim() !== "";
  });
}

/** Read the saved draft, or null when absent / unreadable / corrupt. Returns
 *  Partial so callers defensively default every field (tolerates shape drift). */
export function loadDraft(): Partial<NewJobDraft> | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed as Partial<NewJobDraft>;
  } catch {
    return null;
  }
}

/** Persist the current form. No-op if storage is unavailable. */
export function saveDraft(draft: NewJobDraft): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  } catch {
    /* storage unavailable / quota exceeded — non-fatal; form still works */
  }
}

/** Remove the saved draft. No-op if storage is unavailable. */
export function clearDraft(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* no-op */
  }
}
